import React from "react";

/** Interlaced strapwork: one six-sided strand rotated eight times (build-logo.py `mark_d`). */
const INTERLACED =
  "M48.04 16.90 L83.10 16.90 L83.10 31.20 L76.90 25.00 L76.90 23.10 L41.84 23.10 Z M72.02 25.21 L96.81 50.00 L86.70 60.11 L86.70 51.35 L88.04 50.00 L63.25 25.21 Z M83.10 48.04 L83.10 83.10 L68.80 83.10 L75.00 76.90 L76.90 76.90 L76.90 41.84 Z M74.79 72.02 L50.00 96.81 L39.89 86.70 L48.65 86.70 L50.00 88.04 L74.79 63.25 Z M51.96 83.10 L16.90 83.10 L16.90 68.80 L23.10 75.00 L23.10 76.90 L58.16 76.90 Z M27.98 74.79 L3.19 50.00 L13.30 39.89 L13.30 48.65 L11.96 50.00 L36.75 74.79 Z M16.90 51.96 L16.90 16.90 L31.20 16.90 L25.00 23.10 L23.10 23.10 L23.10 58.16 Z M25.21 27.98 L50.00 3.19 L60.11 13.30 L51.35 13.30 L50.00 11.96 L25.21 36.75 Z";

/** Centre-line squares of the outline form, stroked. */
const SQUARE_A = "M20 20 H80 V80 H20 Z";
const SQUARE_B = "M50 7.57 L92.43 50 L50 92.43 L7.57 50 Z";
const OUTLINE_STROKE = 9;

/** The eight-pointed silhouette. */
const SOLID =
  "M50 7.57 L62.43 20 L80 20 L80 37.57 L92.43 50 L80 62.43 L80 80 L62.43 80 L50 92.43 L37.57 80 L20 80 L20 62.43 L7.57 50 L20 37.57 L20 20 L37.57 20 Z";

export type InnovriddhiForm = "interlaced" | "outline" | "solid" | "auto";

export function innovriddhiFormFor(
  size?: number,
): Exclude<InnovriddhiForm, "auto"> {
  if (size === undefined || size >= 40) return "interlaced";
  if (size >= 20) return "outline";
  return "solid";
}

export interface InnovriddhiMarkProps {
  /** Rendered size in px, both axes; the mark box is square. */
  size?: number;
  title?: string;
  className?: string;
  /** Which drawing to use; `auto` picks from `size`. */
  form?: InnovriddhiForm;
}

export const InnovriddhiMark: React.FC<InnovriddhiMarkProps> = ({
  size = 36,
  title,
  className,
  form = "interlaced",
}) => {
  const resolved = form === "auto" ? innovriddhiFormFor(size) : form;
  return (
    <svg
      className={className}
      viewBox="0 0 100 100"
      width={size}
      height={size}
      fill="none"
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      data-form={resolved}
    >
      {title && <title>{title}</title>}
      {resolved === "interlaced" && <path d={INTERLACED} fill="currentColor" />}
      {resolved === "outline" && (
        <>
          <path
            d={SQUARE_A}
            stroke="currentColor"
            strokeWidth={OUTLINE_STROKE}
            strokeLinejoin="miter"
            strokeMiterlimit={6}
          />
          <path
            d={SQUARE_B}
            stroke="currentColor"
            strokeWidth={OUTLINE_STROKE}
            strokeLinejoin="miter"
            strokeMiterlimit={6}
          />
        </>
      )}
      {resolved === "solid" && <path d={SOLID} fill="currentColor" />}
    </svg>
  );
};
